import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import { registerServiceWorker } from "@/lib/push";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import CreateOrder from "@/pages/CreateOrder";
import OrderDetail from "@/pages/OrderDetail";
import Orders from "@/pages/Orders";
import Leaderboard from "@/pages/Leaderboard";
import Profile from "@/pages/Profile";
import PublicProfile from "@/pages/PublicProfile";
import PublicDonations from "@/pages/PublicDonations";
import Payments from "@/pages/Payments";
import Referrals from "@/pages/Referrals";
import Preferences from "@/pages/Preferences";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Admin from "@/pages/Admin";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import InstallAppButton from "@/components/InstallAppButton";
import ColdStartSplash from "@/components/ColdStartSplash";
import OnboardingTour from "@/components/OnboardingTour";
import LiveActivity from "@/components/LiveActivity";
import { MONETIZATION_ENABLED } from "@/lib/features";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando...</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

function AppShell({ theme, toggleTheme }) {
  const { user } = useAuth();
  const location = useLocation();

  // Handle Emergent Google Auth callback FIRST (before any protected route)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isPublicProfile = location.pathname.startsWith("/u/") || location.pathname === "/doacoes";
  const isPublic = (["/", "/login", "/register"].includes(location.pathname) && !user) || isPublicProfile;
  // Live-activity feels natural on the landing, feed and home. Hide it in
  // admin/settings/onboarding areas where it would be distracting.
  const showLiveActivity = ["/", "/doacoes"].includes(location.pathname)
    || (user && ["/orders"].includes(location.pathname));

  return (
    <>
      {!isPublic && <TopBar theme={theme} onToggleTheme={toggleTheme} />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/esqueci-senha" element={<ForgotPassword />} />
        <Route path="/redefinir-senha" element={<ResetPassword />} />
        <Route path="/u/:userId" element={<PublicProfile />} />
        <Route path="/doacoes" element={<PublicDonations />} />
        <Route
          path="/"
          element={user ? <Home /> : <Landing />}
        />
        <Route
          path="/create"
          element={<ProtectedRoute><CreateOrder /></ProtectedRoute>}
        />
        <Route
          path="/orders"
          element={<ProtectedRoute><Orders /></ProtectedRoute>}
        />
        <Route
          path="/orders/:id"
          element={<ProtectedRoute><OrderDetail /></ProtectedRoute>}
        />
        <Route
          path="/leaderboard"
          element={<ProtectedRoute><Leaderboard /></ProtectedRoute>}
        />
        <Route
          path="/pagamentos"
          element={MONETIZATION_ENABLED ? <ProtectedRoute><Payments /></ProtectedRoute> : <Navigate to="/" replace />}
        />
        <Route
          path="/pagamentos/sucesso"
          element={MONETIZATION_ENABLED ? <ProtectedRoute><Payments /></ProtectedRoute> : <Navigate to="/" replace />}
        />
        <Route
          path="/convites"
          element={<ProtectedRoute><Referrals /></ProtectedRoute>}
        />
        <Route
          path="/especialidades"
          element={<ProtectedRoute><Preferences /></ProtectedRoute>}
        />
        <Route
          path="/profile"
          element={<ProtectedRoute><Profile /></ProtectedRoute>}
        />
        <Route
          path="/admin"
          element={<ProtectedRoute><Admin /></ProtectedRoute>}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isPublicProfile && <BottomNav />}
      {!isPublicProfile && <InstallAppButton />}
      {user && <OnboardingTour user={user} />}
      {showLiveActivity && <LiveActivity />}
    </>
  );
}

function App() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem("rivendas_theme") || "dark";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("rivendas_theme", theme);
  }, [theme]);

  // Register the service worker once (safe on unsupported browsers)
  useEffect(() => {
    registerServiceWorker();
  }, []);

  // Keep-alive ping: while any user has the tab open, hit /api/ every 4
  // minutes so the backend container never goes idle. Skips when the tab
  // is hidden to save bandwidth on mobile.
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    const url = `${backend}/api/`;
    const ping = () => {
      if (document.visibilityState !== "visible") return;
      fetch(url, { method: "GET", cache: "no-store", credentials: "omit" }).catch(() => {});
    };
    ping(); // warm on first mount
    const iv = setInterval(ping, 4 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <div className="App min-h-screen">
      <ColdStartSplash />
      <BrowserRouter>
        <AuthProvider>
          <AppShell theme={theme} toggleTheme={toggleTheme} />
          <Toaster richColors position="bottom-center" theme={theme} duration={2500} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
